/*
 * AVALY cart controller.
 *
 * One owner for cart add/change/remove + drawer rendering. The theme still
 * provides the drawer shell and close behaviour, but this controller intercepts
 * purchase and cart-control events in capture phase so the older theme/custom
 * cart paths cannot double-submit or mutate the wrong Shopify line.
 */
(function () {
  'use strict';

  var CART_ROOT = '[data-quick-cart]';
  var CACHE_MS = 500;
  var DEBOUNCE_MS = 180;
  var REQUEST_TIMEOUT_MS = 7500;
  var CHANGE_SYNC_MS = 700;
  var inflightCart = null;
  var cartCache = null;
  var cartCacheAt = 0;
  var ops = {};
  var timers = {};
  var syncTimer = null;
  var changeQueue = Promise.resolve();
  var addQueue = Promise.resolve();
  var pendingAdds = 0;
  var cartWriteEpoch = 0;
  var cartReadSequence = 0;
  var activeCartWrites = [];
  var lineChangeSeq = {};
  var desiredLineQty = {};
  var rowRefs = {};
  var emittingCartUpdate = false;
  var addProtection = null;
  var renderingCart = false;
  var reclaimTimer = null;
  var cartObserver = null;
  var pendingMutations = [];
  var mutationFailureVersion = 0;
  var checkoutPromise = null;
  var checkoutLocked = false;
  var checkoutNavigationStarted = false;
  var checkoutLinks = new WeakMap();
  var navigationRecoveryTimer = null;
  var uncertainAdd = null;
  var reviewButtonLabels = new WeakMap();

  // Keep checkout behind the entire operation, including debounce and error
  // reconciliation. The drawer's optimistic rendering is not proof of a save.
  function hasPendingChanges() {
    return Object.keys(ops).length > 0 || pendingMutations.length > 0;
  }

  function cartMessage(kind) {
    var panel = cartSelector('[data-cart]') || root();
    if (!panel) return null;
    var selector = '[data-avaly-cart-' + kind + ']';
    var element = $(selector, panel);
    if (!element) {
      element = document.createElement('p');
      element.setAttribute('data-avaly-cart-' + kind, '');
      element.id = 'avaly-cart-' + kind;
      element.className = 'ff-body fs-body-base';
      element.style.margin = '12px 0';
      element.setAttribute('role', kind === 'error' ? 'alert' : 'status');
      element.setAttribute('aria-atomic', 'true');
      if (kind === 'error') element.setAttribute('tabindex', '-1');
      else element.setAttribute('aria-live', 'polite');
      element.hidden = true;
      var items = $('[data-items]', panel);
      panel.insertBefore(element, items || panel.firstChild);
    }
    return element;
  }

  function showCartMessage(kind, message, focus) {
    var element = cartMessage(kind);
    if (!element) return;
    if (element.textContent !== message) element.textContent = message;
    element.hidden = !message;
    if (focus && message) element.focus();
  }

  function updateCheckoutState() {
    var busy = hasPendingChanges() || checkoutLocked || checkoutNavigationStarted;
    // Quantity targets are absolute. Do not accept edits to a pre-add row while
    // an add transaction is still calculating/saving its new quantity.
    $all(CART_ROOT + ' button[data-avaly-increase], ' + CART_ROOT + ' button[data-avaly-decrease], ' + CART_ROOT + ' button[data-avaly-remove]').forEach(function (button) {
      if (pendingAdds && !button.disabled) {
        button.setAttribute('data-avaly-add-locked', '');
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (!pendingAdds && button.hasAttribute('data-avaly-add-locked')) {
        button.removeAttribute('data-avaly-add-locked');
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    });
    $all(CART_ROOT + ' [data-buy-now-button]').forEach(function (link) {
      var original = checkoutLinks.get(link);
      if (!original) {
        original = { href: link.getAttribute('href'), label: link.textContent };
        checkoutLinks.set(link, original);
      }
      // Removing href also prevents middle-click/context-menu navigation from
      // bypassing the save. Preserve keyboard access to the guarded action.
      if (busy) {
        link.removeAttribute('href');
        link.setAttribute('role', 'link');
        link.setAttribute('tabindex', '0');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('aria-busy', 'true');
      } else {
        if (original.href) link.setAttribute('href', original.href);
        link.removeAttribute('aria-disabled');
        link.removeAttribute('aria-busy');
      }
      link.textContent = checkoutNavigationStarted ? 'Opening checkout…' : checkoutLocked ? 'Checking your bag…' : busy ? 'Updating your bag…' : original.label;
    });
    showCartMessage('status', checkoutNavigationStarted ? 'Opening checkout…' : checkoutLocked ? 'Checking your bag before checkout…' : busy ? 'Saving your bag. Please wait…' : '');
    var recovery = cartSelector('[data-avaly-cart-reload]');
    if (recovery && !checkoutNavigationStarted) {
      recovery.hidden = true;
      // The theme's .btn display:flex overrides the browser [hidden] rule.
      recovery.style.display = 'none';
    }
    var review = cartSelector('[data-avaly-cart-review]');
    if (review && !uncertainAdd) {
      review.hidden = true;
      review.style.display = 'none';
    }
  }

  function offerNavigationRecovery() {
    clearTimeout(navigationRecoveryTimer);
    navigationRecoveryTimer = setTimeout(function () {
      if (!checkoutNavigationStarted) return;
      var panel = cartSelector('[data-cart]') || root();
      if (!panel) return;
      var button = $('[data-avaly-cart-reload]', panel);
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn--secondary btn--small';
        button.setAttribute('data-avaly-cart-reload', '');
        button.textContent = 'Reload bag';
        button.addEventListener('click', function () {
          // A fresh page cancels a stalled/cancelled outgoing navigation and
          // rereads Shopify; do not unlock stale state and allow new writes.
          window.location.reload();
        });
        panel.insertBefore(button, $('[data-items]', panel));
      }
      showCartMessage('status', 'Still here? Reload your bag to try checkout again.');
      button.hidden = false;
      button.style.removeProperty('display');
    }, 5000);
  }

  function reportCartFailure(error) {
    mutationFailureVersion += 1;
    showCartMessage('error', error.shopperMessage || 'We could not save your bag. Please check the items and quantities, then try again.');
    if (error.code === 'cart_add_uncertain') {
      var panel = cartSelector('[data-cart]') || root();
      if (!panel) return;
      var review = $('[data-avaly-cart-review]', panel);
      if (!review) {
        review = document.createElement('a');
        review.setAttribute('data-avaly-cart-review', '');
        review.className = 'btn btn--secondary btn--small no-transition';
        review.href = routes().base.replace(/\.js$/, '');
        review.textContent = 'Review full bag';
        panel.insertBefore(review, $('[data-items]', panel));
      }
      review.hidden = false;
      review.style.removeProperty('display');
    }
  }

  function trackMutation(promise) {
    var token = {};
    pendingMutations.push(token);
    function finish() {
      var index = pendingMutations.indexOf(token);
      if (index !== -1) pendingMutations.splice(index, 1);
      updateCheckoutState();
    }
    token.promise = Promise.resolve(promise).then(function (cart) {
      finish();
      return cart;
    }, function (error) {
      reportCartFailure(error);
      finish();
      throw error;
    });
    updateCheckoutState();
    return token.promise;
  }

  function drainCartChanges() {
    Object.keys(ops).forEach(function (key) {
      flushChange(key).catch(function () {});
    });
    var pending = pendingMutations.map(function (token) {
      return token.promise.catch(function () {});
    });
    return Promise.all(pending).then(function () {
      if (hasPendingChanges()) return drainCartChanges();
    });
  }

  function cartFingerprint(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return JSON.stringify({
      total: cart.total_price,
      items: cart.items.map(function (line) {
        return [String(line.key), Number(line.quantity), Number(line.final_line_price)];
      }).sort(function (a, b) { return a[0].localeCompare(b[0]); })
    });
  }

  function prepareCheckout() {
    if (checkoutPromise) return checkoutPromise;
    checkoutLocked = true;
    var failureVersion = mutationFailureVersion;
    showCartMessage('error', '');
    updateCheckoutState();
    var work = drainCartChanges().then(function () {
      if (failureVersion !== mutationFailureVersion) {
        throw new Error('A change could not be saved. Please review your bag, then select Checkout again.');
      }
      var expected = cartFingerprint(cartCache);
      return getCart(true).then(function (cart) {
        if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') {
          throw new Error('We could not confirm your bag. Please try Checkout again.');
        }
        addProtection = null;
        render(cart);
        if (!cart.items.length || cart.item_count < 1) {
          throw new Error('Your bag is empty. Add an item before checking out.');
        }
        if (expected && cartFingerprint(cart) !== expected) {
          throw new Error('Your bag has changed. Please review the updated items and total, then select Checkout again.');
        }
        return cart;
      });
    });
    checkoutPromise = work.then(function (cart) {
      checkoutLocked = false;
      checkoutPromise = null;
      updateCheckoutState();
      return cart;
    }, function (error) {
      checkoutLocked = false;
      checkoutPromise = null;
      updateCheckoutState();
      showCartMessage('error', error.message && /bag|Checkout|checking out/.test(error.message)
        ? error.message : 'We could not confirm your bag. Please check your connection and select Checkout again.', true);
      throw error;
    });
    return checkoutPromise;
  }

  // Drawer and sticky Buy Now share the same server-confirmed navigation owner.
  function checkout(href) {
    var url;
    try { url = new URL(href, window.location.href); } catch (error) { return Promise.reject(error); }
    // Only root or locale-prefixed same-store checkout routes are a handoff;
    // an arbitrary product/page path that ends in "checkout" is not one.
    if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*\/)?(?:checkout|checkouts)\/?$/i.test(url.pathname)) {
      return Promise.reject(new Error('Checkout could not open. Please use this store\'s Checkout link.'));
    }
    if (checkoutNavigationStarted) return Promise.resolve(cartCache);
    return prepareCheckout().then(function (cart) {
      // Concurrent callers may share preparation, but only one may navigate.
      if (checkoutNavigationStarted) return cart;
      checkoutNavigationStarted = true;
      updateCheckoutState();
      offerNavigationRecovery();
      try {
        // Do not replay link.click(): a nested/ignored synthetic activation or
        // the theme's transitionend-only anchor handler can silently swallow
        // checkout. Navigate once, directly, only after server confirmation.
        window.location.assign(url.href);
      } catch (error) {
        checkoutNavigationStarted = false;
        clearTimeout(navigationRecoveryTimer);
        updateCheckoutState();
        showCartMessage('error', 'Checkout could not open. Please select Checkout again.', true);
        throw error;
      }
      return cart;
    });
  }

  function handleCheckout(event) {
    var target = event.target;
    var link = target && target.closest && target.closest(CART_ROOT + ' [data-buy-now-button]');
    if (!link) return;
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    var modifiedClick = event.type === 'auxclick' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    if (modifiedClick && !hasPendingChanges() && !checkoutLocked && !checkoutNavigationStarted) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (modifiedClick) return;
    if (checkoutNavigationStarted || checkoutPromise) return;
    updateCheckoutState();
    var saved = checkoutLinks.get(link);
    checkout(saved && saved.href).catch(function () {});
  }

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function root() {
    return $(CART_ROOT);
  }

  function cartSelector(selector) {
    return $(CART_ROOT + ' ' + selector);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function money(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var value = Number(cents || 0);
    var parts = (value / 100).toFixed(2).split('.');
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*\w+\s*\}\}/, dollars + '.' + parts[1]);
  }

  function parseMoney(text) {
    var matches = String(text || '').match(/\$[\d,]+(?:\.\d{2})?/g);
    if (!matches || !matches.length) return 0;
    var value = Number(matches[matches.length - 1].replace(/[$,]/g, ''));
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  function sizedImage(src, size) {
    if (!src) return '';
    var clean = String(src).replace(/^http(s)?:/, '');
    var match = clean.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?[^#]*)?$/i);
    if (!match) return clean;
    var suffix = match[0];
    var base = clean.slice(0, -suffix.length)
      .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|x\d+)$/i, '');
    return base + '_' + size + suffix;
  }

  function routes() {
    var themeRoutes = window.theme && window.theme.routes && window.theme.routes.cart;
    return {
      base: ((themeRoutes && themeRoutes.base) || '/cart') + '.js',
      add: ((themeRoutes && themeRoutes.add) || '/cart/add') + '.js',
      change: ((themeRoutes && themeRoutes.change) || '/cart/change') + '.js',
      update: ((themeRoutes && themeRoutes.update) || '/cart/update') + '.js'
    };
  }

  function getCart(force) {
    // Wait only for raw writes, never mutation/recovery promises: those can
    // themselves be waiting for this read. A read begun before any write must
    // not later overwrite or return an obsolete cart to .then(render) callers.
    if (activeCartWrites.length) {
      return Promise.all(activeCartWrites.map(function (write) {
        return write.promise.catch(function () {});
      })).then(function () { return getCart(force); });
    }
    var now = Date.now();
    if (!force && cartCache && now - cartCacheAt < CACHE_MS) return Promise.resolve(cartCache);
    if (!force && inflightCart) return inflightCart;

    var epoch = cartWriteEpoch;
    var sequence = ++cartReadSequence;
    var request = requestJson(routes().base, {
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(function (cart) {
        if (epoch !== cartWriteEpoch || sequence !== cartReadSequence) {
          if (inflightCart && inflightCart !== request) return inflightCart;
          return getCart(true);
        }
        return setCachedCart(cart);
      });
    inflightCart = request;
    function clearOwnRead() {
      if (inflightCart === request) inflightCart = null;
    }
    // An older completion must not clear a newer in-flight read.
    request.then(clearOwnRead, clearOwnRead);
    return request;
  }

  function setCachedCart(cart, optimistic) {
    // Debounced and queued quantity intentions remain visible while unrelated
    // authoritative reads or another line's response arrive. Do not apply this
    // overlay to the optimistic click render itself (its new op is set next).
    var quantities = {};
    if (!optimistic) {
      Object.keys(desiredLineQty).forEach(function (key) { quantities[key] = desiredLineQty[key]; });
      Object.keys(ops).forEach(function (key) {
        if (typeof ops[key].optimisticQty === 'number') quantities[key] = ops[key].optimisticQty;
      });
    }
    if (Object.keys(quantities).length && cart && Array.isArray(cart.items)) {
      cart = JSON.parse(JSON.stringify(cart));
      cart.items = cart.items.filter(function (line) {
        if (!Object.prototype.hasOwnProperty.call(quantities, line.key)) return true;
        var quantity = quantities[line.key];
        var delta = quantity - Number(line.quantity || 0);
        cart.item_count = Math.max(0, Number(cart.item_count || 0) + delta);
        cart.total_price = Math.max(0, Number(cart.total_price || 0) + lineUnitPrice(line) * delta);
        cart.original_total_price = Math.max(0, Number(cart.original_total_price || 0) + lineOriginalUnit(line) * delta);
        setLineQty(line, quantity);
        return quantity > 0;
      });
    }
    cartCache = sortCart(cart);
    cartCacheAt = Date.now();
    return cartCache;
  }

  function cloneCart(cart) {
    var source = cart || cartCache || {};
    return JSON.parse(JSON.stringify({
      items: source.items || [],
      item_count: source.item_count || 0,
      currency: source.currency,
      total_price: source.total_price || 0,
      original_total_price: source.original_total_price || 0,
      total_discount: source.total_discount || 0,
      cart_level_discount_applications: source.cart_level_discount_applications || []
    }));
  }

  function json(response) {
    return response.text().then(function (text) {
      var body = {};
      try {
        body = text ? JSON.parse(text) : {};
      } catch (parseError) {
        var htmlError = new Error('Cart request returned a non-JSON response');
        htmlError.status = response.status;
        htmlError.responseText = text ? text.slice(0, 240) : '';
        throw htmlError;
      }
      if (!response.ok || body.status === 422) {
        var error = new Error(body.description || body.message || 'Cart request failed');
        error.status = response.status;
        error.body = body;
        error.cartRejected = !response.ok && response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429;
        throw error;
      }
      return body;
    });
  }

  function requestJson(url, options, timeoutMs) {
    timeoutMs = timeoutMs || REQUEST_TIMEOUT_MS;
    options = options || {};

    var write = options.method && options.method.toUpperCase() !== 'GET' ? {} : null;
    if (write) {
      cartWriteEpoch += 1;
      activeCartWrites.push(write);
    }
    var request = new Promise(function (resolve, reject) {
      var settled = false;
      var controller = window.AbortController ? new AbortController() : null;
      var requestOptions = {};
      Object.keys(options).forEach(function (key) { requestOptions[key] = options[key]; });
      if (controller) requestOptions.signal = controller.signal;

      var timeoutId = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (controller) controller.abort();
        var timeoutError = new Error('Cart request timed out');
        timeoutError.code = 'cart_timeout';
        reject(timeoutError);
      }, timeoutMs);

      try {
        fetch(url, requestOptions)
          .then(json)
          .then(function (body) {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve(body);
          }, function (error) {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            reject(error);
          });
      } catch (error) {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        reject(error);
      }
    });
    if (!write) return request;
    function finishWrite() {
      cartWriteEpoch += 1;
      var index = activeCartWrites.indexOf(write);
      if (index !== -1) activeCartWrites.splice(index, 1);
    }
    write.promise = request.then(function (body) {
      finishWrite();
      return body;
    }, function (error) {
      finishWrite();
      throw error;
    });
    return write.promise;
  }

  function getOrder() {
    try {
      return JSON.parse(localStorage.getItem('neon_cart_order') || '[]');
    } catch (e) {
      return [];
    }
  }

  function setOrder(order) {
    try {
      localStorage.setItem('neon_cart_order', JSON.stringify(order));
    } catch (e) {}
  }

  function rememberAddedVariant(variantId) {
    var id = Number(variantId);
    if (!id) return;
    var existing = getOrder();
    setOrder([id].concat(existing.filter(function (item) { return item !== id; })));
  }

  function sortCart(cart) {
    if (!cart) return cart;
    var order = getOrder();
    var items = cart.items || [];
    cart.sorted = order.length ? items.slice().sort(function (a, b) {
      var ai = order.indexOf(a.variant_id);
      var bi = order.indexOf(b.variant_id);
      if (ai === -1) ai = 9999;
      if (bi === -1) bi = 9999;
      return ai - bi;
    }) : items;
    return cart;
  }

  function lineUnitPrice(line) {
    if (!line) return 0;
    if (typeof line.final_price === 'number') return line.final_price;
    if (typeof line.final_line_price === 'number' && line.quantity) return Math.round(line.final_line_price / line.quantity);
    if (typeof line.line_price === 'number' && line.quantity) return Math.round(line.line_price / line.quantity);
    if (typeof line.price === 'number') return line.price;
    return 0;
  }

  function lineOriginalUnit(line) {
    if (!line) return 0;
    if (typeof line.original_price === 'number') return line.original_price;
    if (typeof line.original_line_price === 'number' && line.quantity) return Math.round(line.original_line_price / line.quantity);
    return lineUnitPrice(line);
  }

  function lineOriginalTotal(line) {
    if (!line) return 0;
    if (typeof line.original_line_price === 'number') return line.original_line_price;
    if (typeof line.original_price === 'number') return line.original_price * Number(line.quantity || 0);
    if (typeof line.final_line_price === 'number') return line.final_line_price;
    return lineOriginalUnit(line) * Number(line.quantity || 0);
  }

  function saleTiers() {
    return window.AVALY_SALE_TIERS || [{ q: 1, pct: 10 }, { q: 2, pct: 20 }, { q: 3, pct: 30 }, { q: 4, pct: 40 }];
  }

  function saleTierFor(units) {
    var tiers = saleTiers();
    var pct = 0;
    tiers.forEach(function (tier) {
      if (units >= Number(tier.q || 0)) pct = Number(tier.pct || 0);
    });
    return pct;
  }

  function isSaleLine(line) {
    var ids = window.AVALY_SALE_PRODUCT_IDS || [];
    return window.AVALY_SALE_SITEWIDE === true || ids.indexOf(line.product_id) !== -1;
  }

  function saleSummary(cart) {
    var items = (cart && cart.items) || [];
    var original = 0;
    var saleOriginal = 0;
    var saleUnits = 0;

    items.forEach(function (line) {
      if (line && line.pending) return;
      var lineOriginal = lineOriginalTotal(line);
      original += lineOriginal;
      if (window.AVALY_SALE_ON && isSaleLine(line)) {
        saleOriginal += lineOriginal;
        saleUnits += Number(line.quantity || 0);
      }
    });

    var pct = window.AVALY_SALE_ON ? saleTierFor(saleUnits) : 0;
    var saleSaving = Math.round(saleOriginal * pct / 100);
    var finalTotal = window.AVALY_SALE_ON && saleUnits > 0
      ? Math.max(0, original - saleSaving)
      : Number((cart && cart.total_price) || 0);

    return {
      original: original,
      finalTotal: finalTotal,
      saleOriginal: saleOriginal,
      saleSaving: saleSaving,
      saleUnits: saleUnits,
      pct: pct
    };
  }

  function subtotalHtml(cart, summary) {
    summary = summary || saleSummary(cart);
    var original = summary.original;
    var finalTotal = summary.finalTotal;

    if (original > finalTotal) {
      return '<span class="quick-cart__subtotal-prices">' +
        '<s class="quick-cart__subtotal-was">' + money(original) + '</s>' +
        '<span class="quick-cart__subtotal-now">' + money(finalTotal) + '</span>' +
      '</span>';
    }

    return money(finalTotal);
  }

  function setLineQty(line, quantity) {
    var unit = lineUnitPrice(line);
    var originalUnit = lineOriginalUnit(line) || unit;
    line.quantity = quantity;
    line.final_line_price = unit * quantity;
    line.line_price = unit * quantity;
    line.original_line_price = originalUnit * quantity;
  }

  function optimisticLineFromQuickAdd(button, variantId, quantity) {
    if (!button || !button.closest) return null;
    var quickAdd = button.closest('.avaly-quick-add');
    var card = button.closest('.avaly-card');
    if (!card) return null;

    var titleEl = $('.avaly-card__title a, .avaly-card__title', card);
    var linkEl = $('.avaly-card__title a, .avaly-card__link', card);
    var imageEl = $('.avaly-card__img', card);
    var priceEl = $('.avaly-card__sale-price', card) || $('.avaly-card__price', card);
    var selectedSize = quickAdd && $('.avaly-quick-add__size.selected', quickAdd);
    var unit = parseMoney(priceEl && priceEl.textContent);
    var qty = Math.max(1, Number(quantity || 1));
    var title = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '';
    var url = linkEl ? linkEl.getAttribute('href') : '';
    var image = imageEl ? (imageEl.currentSrc || imageEl.src || imageEl.getAttribute('src')) : '';
    var imageAlt = imageEl ? imageEl.getAttribute('alt') : '';
    var variantTitle = selectedSize ? selectedSize.textContent.replace(/\s+/g, ' ').trim() : '';

    if (!title || !unit) return null;

    return {
      key: 'optimistic:' + variantId + ':' + Date.now(),
      id: Number(variantId),
      variant_id: Number(variantId),
      quantity: qty,
      product_title: title,
      url: url || '#',
      final_price: unit,
      original_price: unit,
      final_line_price: unit * qty,
      original_line_price: unit * qty,
      line_price: unit * qty,
      image: image,
      featured_image: image ? { url: image, alt: imageAlt || title } : null,
      variant_title: variantTitle,
      options_with_values: variantTitle ? [{ name: 'Size', value: variantTitle }] : [],
      properties: {},
      line_level_discount_allocations: [],
      optimistic: true
    };
  }

  function productDataFromForm(form) {
    var scope = form && form.closest ? form.closest('.product') : null;
    var sectionId = scope && scope.getAttribute('data-section-id');
    var jsonEl = sectionId ? $('#ProductJson-' + sectionId) : null;
    if (!jsonEl) jsonEl = $('script[id^="ProductJson-"]');
    if (!jsonEl) return null;

    try {
      return JSON.parse(jsonEl.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function findVariant(product, variantId) {
    var variants = product && product.variants;
    if (!variants || !variants.length) return null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) return variants[i];
    }
    return null;
  }

  function imageUrlFromValue(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.src || value.url || value.featured_image || '';
  }

  function visibleProductImage(form, product, variant) {
    var scope = form && form.closest ? form.closest('.product') : null;
    var imageEl = scope && $('.product__media-item:not(.hidden) img, .product__media-item img', scope);
    if (imageEl) {
      return {
        url: imageEl.currentSrc || imageEl.src || imageEl.getAttribute('src') || '',
        alt: imageEl.getAttribute('alt') || ''
      };
    }

    var image = imageUrlFromValue(variant && variant.featured_image) ||
      imageUrlFromValue(product && product.featured_image) ||
      imageUrlFromValue(product && product.images && product.images[0]);

    return { url: image, alt: (product && product.title) || '' };
  }

  function optionName(product, index) {
    var option = product && product.options && product.options[index];
    if (!option) return 'Option ' + (index + 1);
    return typeof option === 'string' ? option : (option.name || ('Option ' + (index + 1)));
  }

  function propertiesFromFormData(formData) {
    var properties = {};
    if (!formData || !formData.forEach) return properties;
    formData.forEach(function (value, key) {
      var match = String(key).match(/^properties\[(.+)\]$/);
      if (!match || value == null || value === '') return;
      properties[match[1]] = value;
    });
    return properties;
  }

  function productScope(form) {
    return form && form.closest ? form.closest('.product') : null;
  }

  function productError(form) {
    return $('[data-quantity-error]', productScope(form) || document);
  }

  function showProductError(form, message) {
    var errorEl = productError(form);
    if (!errorEl) return;
    if (message) errorEl.textContent = message;
    errorEl.classList.remove('hidden');
  }

  function hideProductError(form) {
    var errorEl = productError(form);
    if (errorEl) errorEl.classList.add('hidden');
  }

  function inventoryData(form) {
    var jsonEl = $('[data-product-inventory-json]', productScope(form) || document);
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent || '{}').inventory || null;
    } catch (error) {
      return null;
    }
  }

  function currentCartQuantity(variantId, cart) {
    var items = ((cart || cartCache) && (cart || cartCache).items) || [];
    var quantity = 0;
    items.forEach(function (line) {
      if (line && (line.pending || line.optimistic)) return;
      if (String(line.variant_id || line.id) === String(variantId)) {
        quantity += Number(line.quantity || 0);
      }
    });
    return quantity;
  }

  function inventoryLimitError(form, variantId, requestedQty, cart) {
    var inventory = inventoryData(form);
    var variantInventory = inventory && inventory[variantId];
    if (!variantInventory) return '';
    if (variantInventory.inventory_management !== 'shopify') return '';
    if (variantInventory.inventory_policy !== 'deny') return '';

    var available = Number(variantInventory.inventory_quantity);
    if (!Number.isFinite(available)) return '';
    var cartQuantity = currentCartQuantity(variantId, cart);
    var requestedTotal = cartQuantity + Math.max(1, Number(requestedQty || 1));
    if (requestedTotal <= available) return '';
    if (available <= 0) return 'This size is sold out.';
    if (cartQuantity >= available) return 'You already have all available stock for this size in your cart.';
    return 'Only ' + available + ' available for this size.';
  }

  function optimisticLineFromProductForm(form, formData, variantId, quantity) {
    if (!form || !variantId) return null;
    var product = productDataFromForm(form);
    var variant = findVariant(product, variantId);
    var scope = form.closest ? form.closest('.product') : null;
    var titleEl = scope && $('.product__title', scope);
    var priceEl = scope && $('[data-price]', scope);
    var title = (product && product.title) || (titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '');
    var unit = variant && typeof variant.price === 'number' ? variant.price : parseMoney(priceEl && priceEl.textContent);
    var qty = Math.max(1, Number(quantity || 1));
    var image = visibleProductImage(form, product, variant);
    var variantTitle = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
    var options = variant && variant.options && variant.options.length
      ? variant.options.map(function (value, index) {
        return { name: optionName(product, index), value: value };
      })
      : [];
    var url = (product && product.url) || window.location.pathname;

    if (!title || !unit) return null;

    return {
      key: 'optimistic:' + variantId + ':' + Date.now(),
      id: Number(variantId),
      variant_id: Number(variantId),
      product_id: product && product.id,
      quantity: qty,
      product_title: title,
      url: url + (url.indexOf('?') === -1 ? '?variant=' : '&variant=') + encodeURIComponent(variantId),
      final_price: unit,
      original_price: unit,
      final_line_price: unit * qty,
      original_line_price: unit * qty,
      line_price: unit * qty,
      image: image.url,
      featured_image: image.url ? { url: image.url, alt: image.alt || title } : null,
      variant_title: variantTitle,
      options_with_values: options,
      properties: propertiesFromFormData(formData),
      line_level_discount_allocations: [],
      optimistic: true
    };
  }

  function optimisticCartChange(lineKey, delta, remove) {
    if (!cartCache) return null;
    var cart = cloneCart(cartCache);
    var line = findLine(cart, lineKey, null);
    if (!line || line.pending || line.optimistic) return null;

    var oldQty = Number(line.quantity || 0);
    var newQty = remove ? 0 : Math.max(0, oldQty + delta);
    var qtyDelta = newQty - oldQty;
    var originalUnit = lineOriginalUnit(line);
    var unit = window.AVALY_SALE_ON && isSaleLine(line) ? originalUnit : lineUnitPrice(line);

    cart.item_count = Math.max(0, Number(cart.item_count || 0) + qtyDelta);
    cart.total_price = Math.max(0, Number(cart.total_price || 0) + unit * qtyDelta);
    cart.original_total_price = Math.max(0, Number(cart.original_total_price || 0) + originalUnit * qtyDelta);

    if (newQty === 0) {
      cart.items = cart.items.filter(function (item) { return String(item.key) !== String(lineKey); });
    } else {
      setLineQty(line, newQty);
    }

    render(cart, { optimistic: true });
    return newQty;
  }

  function optimisticPendingAdd(quantity) {
    return null;
  }

  function optimisticCartAdd(line) {
    if (!line) return null;
    var cart = cloneCart(cartCache);
    var qty = Math.max(1, Number(line.quantity || 1));
    var unit = lineUnitPrice(line);
    var originalUnit = lineOriginalUnit(line) || unit;

    rememberAddedVariant(line.variant_id || line.id);
    cart.items.unshift(line);
    cart.item_count = Math.max(0, Number(cart.item_count || 0) + qty);
    cart.total_price = Math.max(0, Number(cart.total_price || 0) + unit * qty);
    cart.original_total_price = Math.max(0, Number(cart.original_total_price || 0) + originalUnit * qty);
    protectAddedCart(cart, line.variant_id || line.id);
    render(cart);
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: line, cart: cart, optimistic: true } }));
    return line.key;
  }

  function rollbackPending(pendingKey) {
    if (!pendingKey || !cartCache) return;
    render(removePending(cloneCart(cartCache), pendingKey));
  }

  function recoverCartAfterError(pendingKey) {
    addProtection = null;
    rollbackPending(pendingKey);
    return getCart(true).then(function (cart) { render(cart); }).catch(function () {});
  }

  function flushPendingChangesForVariant(variantId) {
    var id = variantId ? String(variantId) : '';
    var flushed = [];
    Object.keys(ops).forEach(function (lineKey) {
      var op = ops[lineKey];
      if (id && op && op.variantId && String(op.variantId) !== id) return;
      clearTimeout(timers[lineKey]);
      flushed.push(flushChange(lineKey).catch(function () {}));
    });
    var pendingChanges = changeQueue;
    return Promise.all(flushed).then(function () { return pendingChanges.catch(function () {}); });
  }

  function queueAdd(work) {
    // Serialize the full read -> target calculation -> write -> reconciliation,
    // not just POSTs: two simultaneous absolute updates otherwise lose an add.
    pendingAdds += 1;
    var priorChanges = flushPendingChangesForVariant();
    var queued = addQueue.catch(function () {}).then(function () {
      return priorChanges;
    }).then(work);
    function finish() {
      pendingAdds -= 1;
      updateCheckoutState();
    }
    var result = queued.then(function (cart) {
      finish();
      return cart;
    }, function (error) {
      finish();
      throw error;
    });
    addQueue = result.catch(function () {});
    return trackMutation(result);
  }

  function hasLineOptions(formData) {
    var hasOptions = false;
    if (!formData || !formData.forEach) return false;
    formData.forEach(function (value, key) {
      if (hasOptions) return;
      if (key === 'id' || key === 'quantity' || key === 'form_type' || key === 'utf8') return;
      if (value == null || value === '') return;
      hasOptions = true;
    });
    return hasOptions;
  }

  function addLineIdentity(variantId, properties, sellingPlan) {
    var values = Object.keys(properties || {}).filter(function (key) {
      return properties[key] != null && properties[key] !== '';
    }).sort().map(function (key) { return [key, String(properties[key])]; });
    return JSON.stringify([String(variantId), values, String(sellingPlan || '')]);
  }

  function lineAddIdentity(line) {
    var allocation = line.selling_plan_allocation;
    var plan = allocation && allocation.selling_plan;
    return addLineIdentity(line.variant_id || line.id, line.properties, (plan && plan.id) || line.selling_plan);
  }

  function addIntentQuantities(cart) {
    if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') return null;
    var quantities = Object.create(null);
    var count = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      var quantity = Number(line.quantity);
      if (line.pending || line.optimistic || !Number.isInteger(quantity) || quantity <= 0 || !(line.variant_id || line.id)) return null;
      var identity = lineAddIdentity(line);
      quantities[identity] = (quantities[identity] || 0) + quantity;
      count += quantity;
    }
    return count === cart.item_count ? quantities : null;
  }

  function newAddIntent(cart, variantId, quantity, formData) {
    var identity = addLineIdentity(variantId, propertiesFromFormData(formData), formData && formData.get('selling_plan'));
    var expected = addIntentQuantities(cart);
    if (expected) expected[identity] = (expected[identity] || 0) + quantity;
    return { identity: identity, variantId: variantId, quantity: quantity, expected: expected };
  }

  function confirmedAddResult(intent, cart) {
    var actual = addIntentQuantities(cart);
    var expected = intent.expected;
    if (!actual || !expected) return null;
    var keys = Object.keys(expected);
    if (keys.length !== Object.keys(actual).length || keys.some(function (key) { return actual[key] !== expected[key]; })) return null;
    // Ignore discount-sensitive line keys/prices, but require the exact entire
    // quantity/variant/property/plan multiset. Partial matches are not proof.
    var item = cart.items.filter(function (line) { return lineAddIdentity(line) === intent.identity; })[0];
    return item ? { cart: cart, item: item, variantId: intent.variantId, reconciled: true } : null;
  }

  function uncertainAddError() {
    var error = new Error('Your earlier add may already have succeeded. This action only checks the earlier add; it will not add another item. If it cannot be confirmed, open your full bag, check the quantities and reload that page before continuing to shop.');
    error.code = 'cart_add_uncertain';
    error.shopperMessage = error.message;
    return error;
  }

  function performConfirmedAdd(currentCart, variantId, quantity, formData, reviewIntent, send) {
    var intent = newAddIntent(currentCart, variantId, quantity, formData);
    if (uncertainAdd) {
      // A queued, separate add must never consume or supersede an unresolved
      // intent. Only a later explicit review of the same requested item can
      // confirm it, and that review never sends another POST.
      var confirmation = reviewIntent === uncertainAdd && intent.identity === uncertainAdd.identity && quantity === uncertainAdd.quantity
        ? confirmedAddResult(uncertainAdd, currentCart) : null;
      if (!confirmation) return Promise.reject(uncertainAddError());
      uncertainAdd = null;
      $all('button[data-avaly-add-review]').forEach(function (button) {
        clearAddReviewState(button);
        var form = button.closest('form[data-product-form]');
        if (form) hideProductError(form);
      });
      showCartMessage('error', '');
      return Promise.resolve(confirmation);
    }
    return send().catch(function (error) {
      addProtection = null;
      return getCart(true).then(function (cart) {
        render(cart);
        var confirmation = confirmedAddResult(intent, cart);
        if (confirmation) {
          showCartMessage('error', '');
          return confirmation;
        }
        throw error;
      }).catch(function () {
        // Explicit 4xx validation responses are rejected writes, not lost
        // responses. Preserve their stock feedback and a later new action.
        // Timeouts, network/5xx/non-JSON results remain deliberately uncertain.
        if (error.cartRejected) throw error;
        uncertainAdd = intent;
        throw uncertainAddError();
      });
    });
  }

  function setAddReviewState(button, error) {
    if (!button || !error || error.code !== 'cart_add_uncertain') return;
    if (!reviewButtonLabels.has(button)) reviewButtonLabels.set(button, button.innerHTML);
    button.textContent = 'Review bag';
    button.setAttribute('data-avaly-add-review', '');
  }

  function clearAddReviewState(button) {
    if (!button || !reviewButtonLabels.has(button)) return;
    button.innerHTML = reviewButtonLabels.get(button);
    reviewButtonLabels.delete(button);
    button.removeAttribute('data-avaly-add-review');
  }

  function updateCartQuantity(variantId, quantity) {
    var updates = {};
    updates[String(variantId)] = Math.max(0, Number(quantity || 0));
    return requestJson(routes().update, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    });
  }

  function addVariantWithUpdate(variantId, requestedQty, currentCart) {
    var id = String(variantId);
    var quantity = Math.max(1, Number(requestedQty || 1));
    var targetQty = currentCartQuantity(id, currentCart) + quantity;
    var matches = ((currentCart && currentCart.items) || []).filter(function (line) {
      return String(line.variant_id || line.id) === id;
    });
    var ambiguous = matches.length > 1 || matches.some(function (line) {
      return line.selling_plan_allocation || line.selling_plan || Object.keys(line.properties || {}).some(function (key) {
        return line.properties[key] != null && line.properties[key] !== '';
      });
    });

    if (ambiguous) {
      // A variant-ID update changes one matching line, not the aggregate across
      // personalized/plan/discount-split lines. A plain add must add plain units
      // without changing any existing line's properties or selling plan.
      return requestJson(routes().add, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: id, quantity: quantity })
      }).then(function (added) {
        return getCart(true).then(function (cart) {
          var item = findLine(cart, added.key, null);
          if (!item || currentCartQuantity(id, cart) !== targetQty) {
            var error = new Error('We could not confirm the added quantity. Please review your bag and try again.');
            error.status = 422;
            throw error;
          }
          return { cart: cart, item: item, variantId: id };
        });
      });
    }

    return updateCartQuantity(id, targetQty).then(function (cart) {
      var item = findLine(cart, null, id);
      if (!item || currentCartQuantity(id, cart) < targetQty) {
        var error = new Error('We could not update your cart. Please try again.');
        error.status = 422;
        throw error;
      }
      return { cart: cart, item: item, variantId: id, fromUpdate: true };
    });
  }

  function syncCartSoon(delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      getCart(true).then(function (cart) { renderFreshCart(cart); }).catch(function () {});
    }, delay || CHANGE_SYNC_MS);
  }

  function removePending(cart, pendingKey) {
    if (!pendingKey) return cart;
    var removed = null;
    cart.items = (cart.items || []).filter(function (line) {
      var match = String(line.key) === String(pendingKey);
      if (match) removed = line;
      return !match;
    });
    if (removed) {
      var qty = Math.max(0, Number(removed.quantity || 0));
      var unit = lineUnitPrice(removed);
      var originalUnit = lineOriginalUnit(removed) || unit;
      cart.item_count = Math.max(0, Number(cart.item_count || 0) - qty);
      cart.total_price = Math.max(0, Number(cart.total_price || 0) - unit * qty);
      cart.original_total_price = Math.max(0, Number(cart.original_total_price || 0) - originalUnit * qty);
    }
    return cart;
  }

  function mergeAddedItem(item, pendingKey, requestedQuantity) {
    var cart = removePending(cloneCart(cartCache), pendingKey);
    var qty = Math.max(1, Number(requestedQuantity || item.quantity || 1));
    var existing = findLine(cart, item.key, item.variant_id || item.id);
    var unit = lineUnitPrice(item);

    if (existing) {
      setLineQty(existing, Number(existing.quantity || 0) + qty);
    } else {
      item.quantity = qty;
      setLineQty(item, qty);
      cart.items.unshift(item);
    }

    cart.item_count = Math.max(0, Number(cart.item_count || 0) + qty);
    cart.total_price = Math.max(0, Number(cart.total_price || 0) + unit * qty);
    cart.original_total_price = Math.max(0, Number(cart.original_total_price || 0) + lineOriginalTotal(item));
    return sortCart(cart);
  }

  function findLine(cart, key, variantId) {
    var items = (cart && cart.items) || [];
    var i;
    if (key) {
      for (i = 0; i < items.length; i++) {
        if (String(items[i].key) === String(key)) return items[i];
      }
    }
    if (variantId) {
      for (i = 0; i < items.length; i++) {
        if (String(items[i].variant_id || items[i].id) === String(variantId)) return items[i];
      }
    }
    return null;
  }

  function lineQuantity(cart, key, variantId) {
    var line = findLine(cart, key, variantId);
    return line ? Number(line.quantity || 0) : 0;
  }

  function iconPlus() {
    return '<div class="icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" d="M12 5v14M5 12h14"/></svg></div>';
  }

  function iconMinus() {
    return '<div class="icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"/></svg></div>';
  }

  function iconTrash() {
    return '<div class="icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></div>';
  }

  function imageHtml(line) {
    var src = line.featured_image ? line.featured_image.url : line.image;
    var alt = line.featured_image ? line.featured_image.alt : '';
    var sized = sizedImage(src, '240x');
    return sized ? '<img class="image__img" alt="' + escapeHtml(alt || line.product_title) + '" src="' + escapeHtml(sized) + '" />' : '';
  }

  function saleLineSaving(line, summary) {
    if (!summary || !summary.pct || !window.AVALY_SALE_ON || !isSaleLine(line)) return 0;
    return Math.round(lineOriginalTotal(line) * summary.pct / 100);
  }

  function saleLineFinalUnit(line, summary) {
    var originalUnit = lineOriginalUnit(line);
    if (!summary || !summary.pct || !window.AVALY_SALE_ON || !isSaleLine(line)) return lineUnitPrice(line);
    return Math.max(0, Math.round(originalUnit * (100 - summary.pct) / 100));
  }

  function priceHtml(line, summary) {
    var html = '';
    var originalUnit = lineOriginalUnit(line);
    var finalUnit = saleLineFinalUnit(line, summary);
    if (originalUnit > finalUnit) html += '<s class="qty">' + money(originalUnit) + '</s> ';
    html += money(finalUnit);
    if (line.quantity > 1) html += ' <span class="c-subdued">x ' + line.quantity + '</span>';
    return html;
  }

  function optionsHtml(line) {
    var html = '';
    if (line.options_with_values && line.options_with_values.length && line.variant_title) {
      line.options_with_values.forEach(function (option) {
        html += '<div>' + escapeHtml(option.name) + ': ' + escapeHtml(option.value) + '</div>';
      });
    }
    if (line.properties) {
      Object.keys(line.properties).forEach(function (key) {
        if (key.charAt(0) === '_' || line.properties[key] == null || line.properties[key] === '') return;
        html += '<div class="quick-cart__item-properties">' + escapeHtml(key) + ': ' + escapeHtml(line.properties[key]) + '</div>';
      });
    }
    return html;
  }

  function lineDiscountsHtml(line, summary) {
    var saleSaving = saleLineSaving(line, summary);
    if (saleSaving > 0) {
      return '<ul class="quick-cart__item-discounts fs-body-small fs-body-base-ns c-subdued">' +
        '<li>Birthday sale (-' + money(saleSaving) + ')</li>' +
      '</ul>';
    }

    var allocations = line.line_level_discount_allocations || [];
    if (!allocations.length) return '';
    return '<ul class="quick-cart__item-discounts fs-body-small fs-body-base-ns c-subdued">' +
      allocations.map(function (allocation) {
        var title = allocation.discount_application && allocation.discount_application.title;
        return '<li>' + escapeHtml(title || 'Discount') + ' (-' + money(allocation.amount) + ')</li>';
      }).join('') +
      '</ul>';
  }

  function rowHtml(line, summary) {
    var strings = (window.theme && window.theme.strings && window.theme.strings.quickCart) || {};
    if (line.pending) {
      return '' +
        '<div class="quick-cart__item ff-body fs-body-base is-updating" data-line-key="' + escapeHtml(line.key) + '">' +
          '<div class="quick-cart__item-left"><div class="quick-cart__image"></div></div>' +
          '<div class="quick-cart__item-middle">' +
            '<h4 class="fs-body-bold">' + escapeHtml(line.product_title) + '</h4>' +
            '<div class="c-subdued">Updating your cart...</div>' +
          '</div>' +
        '</div>';
    }
    var key = escapeHtml(line.key);
    var variantId = escapeHtml(line.variant_id || line.id);
    var disabledAttrs = line.optimistic ? ' disabled aria-disabled="true" tabindex="-1"' : '';
    var controlsHtml =
      '<div class="quick-cart__control">' +
        '<div class="quick-cart__control-top">' +
          '<button type="button" class="quick-cart__button quick-cart__button-increase" data-avaly-increase aria-label="' + escapeHtml(strings.addProductQuantity || 'Add product quantity') + '"' + disabledAttrs + '>' + iconPlus() + '</button>' +
          '<button type="button" class="quick-cart__button quick-cart__button-decrease" data-avaly-decrease aria-label="' + escapeHtml(strings.removeProductQuantity || 'Subtract product quantity') + '"' + disabledAttrs + '>' + iconMinus() + '</button>' +
          '<button type="button" class="quick-cart__button quick-cart__button-remove" data-avaly-remove aria-label="' + escapeHtml(strings.removeProduct || 'Remove') + '"' + disabledAttrs + '>' + iconTrash() + '</button>' +
        '</div>' +
      '</div>';

    return '' +
      '<div class="quick-cart__item ff-body fs-body-base' + (line.optimistic ? ' is-updating' : '') + '" data-line-key="' + key + '" data-id="' + variantId + '" data-quantity="' + line.quantity + '">' +
        '<div class="quick-cart__item-left">' +
          '<a href="' + escapeHtml(line.url) + '"><div class="quick-cart__image">' + imageHtml(line) + '</div></a>' +
        '</div>' +
        '<div class="quick-cart__item-middle">' +
          '<h4 class="fs-body-bold"><a href="' + escapeHtml(line.url) + '">' + escapeHtml(line.product_title) + '</a></h4>' +
          '<div>' + priceHtml(line, summary) + '</div>' +
          optionsHtml(line) +
          lineDiscountsHtml(line, summary) +
        '</div>' +
        controlsHtml +
      '</div>';
  }

  function cartDiscountsHtml(cart) {
    var apps = cart.cart_level_discount_applications || [];
    if (!apps.length) return '';
    return '<ul>' + apps.map(function (app) {
      return '<li>' + escapeHtml(app.title || 'Discount') + ' (-' + money(app.total_allocated_amount) + ')</li>';
    }).join('') + '</ul>';
  }

  function captureCartFocus(itemsEl) {
    var drawer = root();
    var active = document.activeElement;
    if (!drawer || !drawer.classList.contains('visible') || !itemsEl || !active || !itemsEl.contains(active)) return null;
    var row = active.closest('[data-line-key]');
    if (!row) return null;
    var key = row.getAttribute('data-line-key');
    var line = findLine(cartCache, key, null);
    var controls = $all('button, a', row);
    return {
      element: active, key: key, identity: line && lineAddIdentity(line),
      rowIndex: $all('[data-line-key]', itemsEl).indexOf(row),
      controlIndex: controls.indexOf(active),
      action: ['data-avaly-increase', 'data-avaly-decrease', 'data-avaly-remove'].filter(function (name) { return active.hasAttribute(name); })[0]
    };
  }

  function restoreCartFocus(saved, itemsEl, cart) {
    var drawer = root();
    if (!saved || !drawer || !drawer.classList.contains('visible')) return;
    if (document.activeElement !== document.body && document.activeElement !== saved.element) return;
    var rows = $all('[data-line-key]', itemsEl);
    var row = rows.filter(function (item) { return item.getAttribute('data-line-key') === saved.key; })[0];
    if (!row && saved.identity) {
      var matches = (cart.items || []).filter(function (line) { return lineAddIdentity(line) === saved.identity; });
      if (matches.length === 1) row = rows.filter(function (item) { return item.getAttribute('data-line-key') === String(matches[0].key); })[0];
    }
    if (!row) row = rows[Math.min(saved.rowIndex, rows.length - 1)];
    var target = row && (saved.action ? $('[' + saved.action + ']:not(:disabled)', row) : $all('button:not(:disabled), a[href]', row)[saved.controlIndex]);
    if (!target && row) target = $('button:not(:disabled), a[href]', row);
    if (!target && !rows.length) {
      var empty = cartSelector('[data-empty]');
      target = empty && $('a[href], button:not(:disabled)', empty);
      if (!target && empty) {
        empty.setAttribute('tabindex', '-1');
        target = empty;
      }
    }
    if (!target) target = $('[data-close-icon]', drawer);
    if (target) target.focus({ preventScroll: true });
  }

  function render(cart, options) {
    options = options || {};
    var savedFocus = captureCartFocus(cartSelector('[data-items]'));
    cart = setCachedCart(cart, options.optimistic === true);

    var itemsEl = cartSelector('[data-items]');
    var emptyEl = cartSelector('[data-empty]');
    var footerEl = cartSelector('[data-footer]');
    var subtotalEl = cartSelector('[data-subtotal]');
    var discountsEl = cartSelector('[data-discounts]');
    var rows = cart.sorted || cart.items || [];
    var realRows = rows.filter(function (line) { return !(line && (line.pending || line.optimistic)); });
    var summary = saleSummary(cart);

    renderingCart = true;
    try {
      if (itemsEl) itemsEl.innerHTML = rows.map(function (line) { return rowHtml(line, summary); }).join('');
      if (subtotalEl) subtotalEl.innerHTML = subtotalHtml(cart, summary);
      if (discountsEl) {
        discountsEl.innerHTML = cartDiscountsHtml(cart);
        discountsEl.classList.toggle('visible', Boolean(cart.cart_level_discount_applications && cart.cart_level_discount_applications.length));
      }
      if (emptyEl) emptyEl.classList.toggle('visible', rows.length === 0);
      if (footerEl) footerEl.classList.toggle('visible', realRows.length > 0);
      $all('[data-js-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
      });
    } finally {
      setTimeout(function () { renderingCart = false; }, 0);
    }

    updateCheckoutState();
    restoreCartFocus(savedFocus, itemsEl, cart);

    document.dispatchEvent(new CustomEvent('quick-cart:updated', { detail: { cart: cart } }));
    if (options.emit === true) {
      emittingCartUpdate = true;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      emittingCartUpdate = false;
    }
  }

  function hasLegacyCartRows() {
    var itemsEl = cartSelector('[data-items]');
    if (!itemsEl) return false;
    return $all('.quick-cart__item', itemsEl).some(function (row) {
      return !row.hasAttribute('data-line-key') || !row.querySelector('[data-avaly-increase],[data-avaly-decrease],[data-avaly-remove]');
    });
  }

  function reclaimCartRows(force) {
    clearTimeout(reclaimTimer);
    reclaimTimer = setTimeout(function () {
      if (renderingCart) return;
      if (!hasLegacyCartRows()) return;
      if (cartCache && !force) {
        render(cartCache, { emit: false });
        return;
      }
      getCart(true).then(function (cart) { render(cart, { emit: false }); }).catch(function () {});
    }, 20);
  }

  function observeCartRows() {
    var itemsEl = cartSelector('[data-items]');
    if (!itemsEl || !window.MutationObserver || cartObserver) return;
    cartObserver = new MutationObserver(function () {
      if (!renderingCart) reclaimCartRows(false);
    });
    cartObserver.observe(itemsEl, { childList: true });
  }

  function protectAddedCart(cart, variantId) {
    addProtection = {
      until: Date.now() + REQUEST_TIMEOUT_MS + 1000,
      minCount: Number((cart && cart.item_count) || 0),
      variantId: variantId ? String(variantId) : ''
    };
  }

  function isProtectedStale(cart) {
    if (!addProtection) return false;
    if (Date.now() > addProtection.until) {
      addProtection = null;
      return false;
    }
    if (Number((cart && cart.item_count) || 0) < addProtection.minCount) return true;
    if (addProtection.variantId && !findLine(cart, null, addProtection.variantId)) return true;
    return false;
  }

  function renderFreshCart(cart) {
    if (isProtectedStale(cart)) return false;
    render(cart);
    return true;
  }

  function syncAfterAdd(expectedCart, variantId, attempt) {
    attempt = attempt || 0;
    getCart(true).then(function (cart) {
      if (renderFreshCart(cart)) return;
      if (attempt < 4) {
        setTimeout(function () {
          syncAfterAdd(expectedCart, variantId, attempt + 1);
        }, 220 + attempt * 180);
      }
    }).catch(function () {});
  }

  function openDrawer(cart) {
    var el = root();
    if (!el) return;
    var cartPanel = $('[data-cart]', el);
    el.classList.add('active');
    if (cartPanel) cartPanel.classList.add('visible');
    setTimeout(function () {
      el.classList.add('visible');
      document.dispatchEvent(new CustomEvent('quick-cart:open', { detail: { cart: cartCache || cart } }));
    }, 50);
  }

  function setButtonState(button, state) {
    if (!button) return;
    if (state === 'loading') {
      button.disabled = true;
      button.classList.add('loading');
      button.classList.remove('item-added');
      return;
    }
    if (state === 'added') {
      button.disabled = false;
      button.classList.remove('loading');
      button.classList.add('item-added');
      setTimeout(function () { button.classList.remove('item-added'); }, 1800);
      return;
    }
    button.disabled = false;
    button.classList.remove('loading', 'item-added');
  }

  function closeQuickAddModal(context) {
    var modal = context && context.closest && context.closest('[data-quick-add-modal]');
    if (!modal) return;
    var closeButton = $('[data-quick-add-modal-close]', modal);
    if (closeButton) {
      closeButton.click();
      return;
    }
    modal.classList.remove('is-visible', 'active');
    setTimeout(function () { modal.classList.remove('is-fixed'); }, 300);
  }

  function addForm(form, button) {
    if (checkoutLocked || checkoutNavigationStarted) return Promise.reject(new Error('Please wait while we check your bag.'));
    setButtonState(button, 'loading');
    var formData = new FormData(form);
    var requestedQty = Math.max(1, Number(formData.get('quantity') || 1));
    var variantId = formData.get('id');
    var reviewIntent = uncertainAdd;
    hideProductError(form);
    var pendingKey = uncertainAdd ? null : optimisticCartAdd(optimisticLineFromProductForm(form, formData, variantId, requestedQty)) || optimisticPendingAdd(requestedQty);
    var body = new URLSearchParams(formData).toString();
    var sendAdd = function (currentCart) {
      if (!hasLineOptions(formData)) {
        return addVariantWithUpdate(variantId, requestedQty, currentCart);
      }
      return requestJson(routes().add, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: body
      });
    };
    openDrawer(cartCache);
    return queueAdd(function () {
      return getCart(true)
      .then(function (cart) {
        if (uncertainAdd) return performConfirmedAdd(cart, variantId, requestedQty, formData, reviewIntent, function () {});
        var inventoryError = inventoryLimitError(form, variantId, requestedQty, cart);
        if (inventoryError) {
          var error = new Error(inventoryError);
          error.status = 422;
          throw error;
        }
        return performConfirmedAdd(cart, variantId, requestedQty, formData, reviewIntent, function () { return sendAdd(cart); });
      })
      .then(function (result) {
        var item = result && result.item ? result.item : result;
        rememberAddedVariant(item.variant_id);
        var optimisticCart = result && result.cart ? sortCart(result.cart) : mergeAddedItem(item, pendingKey, requestedQty);
        render(optimisticCart);
        protectAddedCart(optimisticCart, item.variant_id);
        closeQuickAddModal(form);
        clearAddReviewState(button);
        setButtonState(button, 'added');
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: item, cart: optimisticCart, skipConfetti: Boolean(pendingKey) } }));
        syncAfterAdd(optimisticCart, item.variant_id, 0);
        return optimisticCart;
      })
      .catch(function (error) {
        if (uncertainAdd) error = uncertainAddError();
        setButtonState(button, 'idle');
        setAddReviewState(button, error);
        showProductError(form, error.shopperMessage || (error && error.status === 422 ? error.message : 'We could not update your cart. Please try again.'));
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { error: error, errorMessage: error.message } }));
        return recoverCartAfterError(pendingKey).then(function () { throw error; });
      });
    });
  }

  function addVariant(variantId, quantity, button, optimisticLine) {
    if (checkoutLocked || checkoutNavigationStarted) return Promise.reject(new Error('Please wait while we check your bag.'));
    setButtonState(button, 'loading');
    var requestedQty = Math.max(1, Number(quantity || 1));
    var reviewIntent = uncertainAdd;
    var pendingKey = uncertainAdd ? null : optimisticCartAdd(optimisticLine) || optimisticPendingAdd(requestedQty);
    var sendAdd = function (currentCart) {
      return addVariantWithUpdate(variantId, requestedQty, currentCart);
    };
    openDrawer(cartCache);
    return queueAdd(function () {
      return getCart(true)
      .then(function (cart) {
        return performConfirmedAdd(cart, variantId, requestedQty, null, reviewIntent, function () { return sendAdd(cart); });
      })
      .then(function (result) {
        var item = result && result.item ? result.item : result;
        rememberAddedVariant(item.variant_id || variantId);
        var optimisticCart = result && result.cart ? sortCart(result.cart) : mergeAddedItem(item, pendingKey, requestedQty);
        render(optimisticCart);
        protectAddedCart(optimisticCart, item.variant_id || variantId);
        clearAddReviewState(button);
        setButtonState(button, 'added');
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: item, cart: optimisticCart, skipConfetti: Boolean(optimisticLine) } }));
        syncAfterAdd(optimisticCart, item.variant_id || variantId, 0);
        return optimisticCart;
      })
      .catch(function (error) {
        if (uncertainAdd) error = uncertainAddError();
        setButtonState(button, 'idle');
        setAddReviewState(button, error);
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { error: error, errorMessage: error.message } }));
        return recoverCartAfterError(pendingKey).then(function () { throw error; });
      });
    });
  }

  function scheduleChange(lineKey) {
    clearTimeout(timers[lineKey]);
    timers[lineKey] = setTimeout(function () { flushChange(lineKey).catch(function () {}); }, DEBOUNCE_MS);
    updateCheckoutState();
  }

  function flushChange(lineKey) {
    var op = ops[lineKey];
    clearTimeout(timers[lineKey]);
    delete timers[lineKey];
    if (!op) return Promise.resolve();
    delete ops[lineKey];

    if (typeof op.optimisticQty !== 'number') {
      return trackMutation(getCart(true).then(function (cart) {
        var line = findLine(cart, lineKey, op.variantId);
        if (!line) {
          renderFreshCart(cart);
          return null;
        }
        var qty = op.remove ? 0 : Math.max(0, Number(line.quantity || 0) + op.delta);
        return changeLine(line.key, qty, op.variantId);
      }).catch(function (error) {
        return getCart(true).then(function (cart) { renderFreshCart(cart); }).catch(function () {}).then(function () { throw error; });
      }));
    }

    return changeLine(lineKey, op.optimisticQty, op.variantId);
  }

  function changeLine(lineKey, quantity, variantId) {
    var seq = (lineChangeSeq[lineKey] || 0) + 1;
    lineChangeSeq[lineKey] = seq;
    desiredLineQty[lineKey] = quantity;

    var line = findLine(cartCache, lineKey, null);
    var title = line && line.product_title;
    var work = changeQueue.catch(function () {}).then(function () {
      return requestJson(routes().change, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
    }).then(function (cart2) {
        if (seq !== lineChangeSeq[lineKey]) return cart2;
        if (desiredLineQty[lineKey] !== quantity) return cart2;

        delete desiredLineQty[lineKey];
        var confirmedLine = findLine(cart2, lineKey, null);
        if (!confirmedLine && quantity > 0) {
          // Shopify can change a line key after a discount changes. A variant
          // fallback is safe only when it identifies exactly one remaining line.
          var matches = (cart2.items || []).filter(function (item) {
            return String(item.variant_id || item.id) === String(variantId);
          });
          if (matches.length === 1) confirmedLine = matches[0];
        }
        if (Number(confirmedLine && confirmedLine.quantity || 0) !== quantity) {
          // A 200 response can still clamp quantity to available stock. Do not
          // repeatedly POST the same rejected quantity or proceed silently.
          addProtection = null;
          render(cart2);
          var mismatch = new Error('Requested quantity was not saved');
          mismatch.shopperMessage = 'The quantity' + (title ? ' for ' + title : '') + ' could not be saved. Please review the available quantity in your bag.';
          throw mismatch;
        }
        if (renderFreshCart(cart2)) {
          syncCartSoon();
        } else {
          syncCartSoon(1200);
        }
        return cart2;
      })
      .catch(function (error) {
        if (seq === lineChangeSeq[lineKey]) {
          delete desiredLineQty[lineKey];
          error.shopperMessage = error.shopperMessage || 'We could not save the quantity' + (title ? ' for ' + title : '') + '. Please review your bag and try again.';
          addProtection = null;
          return getCart(true).then(function (cart) { render(cart); }).catch(function () {}).then(function () { throw error; });
        }
        throw error;
      });

    changeQueue = work.catch(function () {});
    return trackMutation(work);
  }

  function handleCartControl(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('button[data-avaly-increase],button[data-avaly-decrease],button[data-avaly-remove],button[data-increase],button[data-decrease],button[data-remove]');
    if (!button || !button.closest(CART_ROOT)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutLocked || checkoutNavigationStarted || pendingAdds) return;
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    var row = button.closest('.quick-cart__item');
    var lineKey = row && row.getAttribute('data-line-key');
    var variantId = row && row.getAttribute('data-id');

    if (!lineKey) {
      variantId = button.getAttribute('data-increase') || button.getAttribute('data-decrease') || button.getAttribute('data-remove') || variantId;
      var line = findLine(cartCache, null, variantId);
      lineKey = line && line.key;
    }
    if (!lineKey) return;

    addProtection = null;
    rowRefs[lineKey] = row;
    var op = ops[lineKey] || { delta: 0, remove: false, variantId: variantId };
    var clickDelta = 0;
    if (button.hasAttribute('data-avaly-remove') || button.hasAttribute('data-remove')) {
      op.remove = true;
    } else if (button.hasAttribute('data-avaly-increase') || button.hasAttribute('data-increase')) {
      clickDelta = 1;
      op.delta += 1;
    } else {
      clickDelta = -1;
      op.delta -= 1;
    }
    var optimisticQty = optimisticCartChange(lineKey, clickDelta, op.remove);
    if (optimisticQty === null && row) {
      optimisticQty = op.remove ? 0 : Math.max(0, Number(row.getAttribute('data-quantity') || 0) + op.delta);
    }
    op.optimisticQty = optimisticQty;
    ops[lineKey] = op;
    scheduleChange(lineKey);
  }

  function handleProductSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-product-form]')) return;
    var button = $('[data-add-to-cart]', form);
    if (button && button.disabled) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addForm(form, button).catch(function () {});
  }

  function handleProductAddClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var button = target.closest('button[data-add-to-cart]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    var form = button.closest('form[data-product-form]');
    if (!form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addForm(form, button).catch(function () {});
  }

  function handleQuickAddSize(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var sizeButton = target.closest('.avaly-quick-add__size');
    if (!sizeButton || sizeButton.disabled || sizeButton.classList.contains('avaly-quick-add__size--unavailable')) return;

    var quickAdd = sizeButton.closest('.avaly-quick-add');
    if (!quickAdd) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    $all('.avaly-quick-add__size', quickAdd).forEach(function (button) {
      button.classList.remove('selected');
    });
    sizeButton.classList.add('selected');

    var addButton = $('.avaly-quick-add__btn', quickAdd);
    if (addButton) {
      addButton.dataset.variantId = sizeButton.dataset.variantId;
      addButton.disabled = false;
      addButton.textContent = 'Add to Cart';
    }
  }

  function handleQuickAdd(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var avalyButton = target.closest('.avaly-quick-add__btn');
    if (avalyButton) {
      var variantId = avalyButton.dataset.variantId;
      if (!variantId || avalyButton.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      var originalText = avalyButton.textContent;
      avalyButton.disabled = true;
      avalyButton.textContent = 'Adding...';
      avalyButton.classList.add('avaly-quick-add__btn--adding');
      addVariant(variantId, 1, null, optimisticLineFromQuickAdd(avalyButton, variantId, 1)).then(function () {
        avalyButton.textContent = 'Added!';
        avalyButton.classList.remove('avaly-quick-add__btn--adding');
        avalyButton.classList.add('avaly-quick-add__btn--added');
        setTimeout(function () {
          avalyButton.disabled = false;
          avalyButton.classList.remove('avaly-quick-add__btn--added');
          avalyButton.textContent = originalText === 'Adding...' || originalText === 'Review bag' ? 'Add to Cart' : originalText;
        }, 1600);
      }).catch(function (error) {
        avalyButton.disabled = false;
        avalyButton.classList.remove('avaly-quick-add__btn--adding');
        avalyButton.textContent = originalText === 'Review bag' ? 'Add to Cart' : originalText;
        setAddReviewState(avalyButton, error);
        if (error.code !== 'cart_add_uncertain') avalyButton.textContent = 'Error - Try Again';
        if (error.code !== 'cart_add_uncertain') setTimeout(function () { avalyButton.textContent = originalText || 'Add to Cart'; }, 1800);
      });
      return;
    }

    var themeButton = target.closest('button[data-quick-add][data-product-id]:not([data-open-modal])');
    if (!themeButton) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    var quantityInput = $('[data-quantity-input]', themeButton.parentNode);
    addVariant(themeButton.dataset.productId, quantityInput ? quantityInput.value : 1, themeButton).catch(function () {});
  }

  document.addEventListener('click', handleCheckout, true);
  document.addEventListener('auxclick', handleCheckout, true);
  document.addEventListener('keydown', handleCheckout, true);
  document.addEventListener('submit', handleProductSubmit, true);
  document.addEventListener('click', handleProductAddClick, true);
  document.addEventListener('click', handleCartControl, true);
  document.addEventListener('click', handleQuickAddSize, true);
  document.addEventListener('click', handleQuickAdd, true);

  document.addEventListener('cart:updated', function (event) {
    if (emittingCartUpdate) return;
    if (event && event.detail && event.detail.cart && !isProtectedStale(event.detail.cart)) {
      render(event.detail.cart, { emit: false });
    }
  });

  // Back from checkout may restore this same JS heap from the browser cache.
  // Release the one-navigation latch; the next checkout still rechecks Shopify.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    checkoutNavigationStarted = false;
    clearTimeout(navigationRecoveryTimer);
    updateCheckoutState();
  });

  function initCart() {
    observeCartRows();
    getCart(false).then(function (cart) {
      render(cart, { emit: false });
      setTimeout(function () { if (cartCache) render(cartCache, { emit: false }); }, 450);
      setTimeout(function () { if (cartCache) render(cartCache, { emit: false }); }, 1400);
    }).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCart);
  } else {
    initCart();
  }

  window.AvalyCart = {
    addVariant: addVariant,
    getCart: getCart,
    open: openDrawer,
    render: render,
    prepareCheckout: prepareCheckout,
    checkout: checkout,
    hasPendingChanges: hasPendingChanges
  };
})();
